import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreateMutation } from "../../api/genericHooks";
import { BookContext } from "../../contexts/BookContext";
import { PlayButton } from "../Buttons/PlayButton";
import { SimpleAudioProgress } from "../SimpleAudioProgress";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
};

export const ValidateAudioBookModal: FC<Props> = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const { generated_chapters, refresh } = useContext(BookContext);
  const [selectedChapterIds, setSelectedChapterIds] = useState<string[]>([]);
  const [consent, setConsent] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const handleCheckboxChange = (chapterId: string, isChecked: boolean) => {
    if (isChecked) {
      setConsent(false);
      setSelectedChapterIds((prev) => [...prev, chapterId]);
    } else {
      setSelectedChapterIds((prev) => prev.filter((id) => id !== chapterId));
    }
  };

  const validateAudioBookMutation = useCreateMutation("/validate_audio_book");

  const handleSubmit = async () => {
    if (selectedChapterIds.length === 0 && !consent) {
      setError(true);
      return;
    }

    try {
      await validateAudioBookMutation
        .mutateAsync({
          generated_chapter_ids: selectedChapterIds,
          validate_audio_book: consent,
        })
        .then(() => {
          toast.success(
            t(
              `audioBook.${
                consent ? "validatedAudioBookSucess" : "recreatedChaptersSucess"
              }`
            )
          );
          setOpen(false);
          refresh();
        });
    } catch (error) {
      console.error("Error validating audio book:", error);
    }
  };

  const button = () => {
    if (selectedChapterIds.length === 0) {
      return (
        <button
          type="button"
          onClick={() => handleSubmit()}
          className="inline-flex w-full justify-center rounded bg-rust-500 px-4 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto"
        >
          {t("audioBook.validate")}
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => handleSubmit()}
        className="inline-flex w-full justify-center rounded bg-delicate-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-delicate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-delicate-500 sm:w-auto"
      >
        {t("audioBook.recreateChapters")}
      </button>
    );
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-slate-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen flex items-center justify-center">
        <div className="flex h-content items-end justify-center p-2 sm:p-4 text-center sm:items-center w-full">
          <DialogPanel
            transition
            className="h-full relative transform rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left w-full sm:w-1/2 sm:max-w-1/2 shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon
                  aria-hidden="true"
                  className="w-5 h-5 sm:w-6 sm:h-6"
                />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <DialogTitle
                  as="h3"
                  className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                >
                  {t("audioBook.validateAudioBook")}
                </DialogTitle>
                <hr className="mt-2 w-full border-slate-200" />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row mt-2 justify-around h-full">
              <div
                className="text-sm sm:text-base"
                dangerouslySetInnerHTML={{
                  __html: t("audioBook.validateAudioBookDescription", {
                    interpolation: {
                      escapeValue: false,
                    },
                  }),
                }}
              />
            </div>
            <div className="flex flex-col mt-2 space-y-4 h-60 overflow-y-auto border border-slate-200 rounded-lg p-4">
              {generated_chapters.map((generated_chapter) => (
                <div
                  key={generated_chapter.title}
                  className="flex flex-col border-b bg-white p-3 sm:p-4 w-full space-y-2"
                >
                  <div className="flex flex-row items-center justify-between w-full">
                    <div className="flex flex-row items-center space-x-3 flex-1">
                      <div className="group grid size-4 grid-cols-1 flex-shrink-0">
                        <input
                          id={generated_chapter.title}
                          name={generated_chapter.title}
                          checked={selectedChapterIds.includes(
                            String(generated_chapter.id)
                          )}
                          onChange={(e) =>
                            handleCheckboxChange(
                              String(generated_chapter.id),
                              e.target.checked
                            )
                          }
                          type="checkbox"
                          className="col-start-1 row-start-1 appearance-none rounded border border-slate-300 bg-white checked:border-delicate-800 checked:bg-delicate-800 indeterminate:border-delicate-800 indeterminate:bg-delicate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-delicate-800 disabled:border-slate-300 disabled:bg-slate-100 disabled:checked:bg-slate-100 forced-colors:appearance-auto"
                        />
                        <svg
                          fill="none"
                          viewBox="0 0 14 14"
                          className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-slate-950/25"
                        >
                          <path
                            d="M3 8L6 11L11 3.5"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="opacity-0 group-has-[:checked]:opacity-100"
                          />
                          <path
                            d="M3 7H11"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="opacity-0 group-has-[:indeterminate]:opacity-100"
                          />
                        </svg>
                      </div>
                      <div className="flex flex-col items-start justify-between text-xs sm:text-sm text-slate-800 font-semibold">
                        {generated_chapter.title}
                      </div>
                    </div>
                    <div className="flex flex-row items-center justify-end">
                      {generated_chapter.audio_url && (
                        <PlayButton
                          audioUrl={generated_chapter.audio_url}
                          size={6}
                          iconSize={4}
                        />
                      )}
                    </div>
                  </div>
                  {generated_chapter.audio_url && (
                    <div className="w-full px-6">
                      <SimpleAudioProgress
                        audioUrl={generated_chapter.audio_url}
                        className=""
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {selectedChapterIds.length === 0 && (
              <>
                <div className="flex flex-row items-center mt-4 space-x-4">
                  <div className="group grid size-4 grid-cols-1 flex-shrink-0">
                    <input
                      id="consent"
                      name="consent"
                      checked={consent}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setError(false);
                        }
                        setConsent(e.target.checked);
                      }}
                      type="checkbox"
                      className="col-start-1 row-start-1 appearance-none rounded border border-slate-300 bg-white checked:border-delicate-800 checked:bg-delicate-800 indeterminate:border-delicate-800 indeterminate:bg-delicate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-delicate-800 disabled:border-slate-300 disabled:bg-slate-100 disabled:checked:bg-slate-100 forced-colors:appearance-auto"
                    />
                    <svg
                      fill="none"
                      viewBox="0 0 14 14"
                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-slate-950/25"
                    >
                      <path
                        d="M3 8L6 11L11 3.5"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="opacity-0 group-has-[:checked]:opacity-100"
                      />
                      <path
                        d="M3 7H11"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="opacity-0 group-has-[:indeterminate]:opacity-100"
                      />
                    </svg>
                  </div>
                  <label htmlFor="consent" className="cursor-pointer flex-1">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: t("audioBook.consent", {
                          interpolation: {
                            escapeValue: false,
                          },
                        }),
                      }}
                    />
                  </label>
                </div>
                <div className="flex flex-row items-center mt-4 space-x-4">
                  {error && (
                    <p className="text-red-500">
                      {t("audioBook.consentRequired")}
                    </p>
                  )}
                </div>
              </>
            )}
            <div className="flex flex-row items-center justify-end mt-4 space-x-4">
              {button()}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
